import React from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner } from 'react-bootstrap';
import Flex from 'components/common/Flex';
import { getColor } from 'helpers/utils';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import BasicECharts from 'components/common/BasicEChart';
import { progressPercentStatus } from 'data/common/common';
import _ from 'lodash';
import Background from 'components/common/Background';
import corner6 from 'assets/img/illustrations/corner-6.png';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

const getOptions = (data, keys) => ({
  tooltip: {
    trigger: 'axis',
    formatter: '<strong>{b0}</strong> : {c0}'
  },
  xAxis: {
    data: keys
  },
  series: [
    {
      type: 'bar',
      showBackground: true,
      backgroundStyle: {
        borderRadius: 10
      },
      barWidth: '5px',
      itemStyle: {
        borderRadius: 10,
        color: getColor('primary')
      },
      data,
      z: 10
    }
  ],
  grid: {
    bottom: '2%',
    top: '2%',
    right: '10px',
    left: '10px'
  }
});

const AdminDashboardMetricCard = ({
  title,
  totalCount,
  chartData,
  loading,
  error
}) => {
  const dataValues = _.values(chartData || {});
  const dataKeys = _.keys(chartData || {});
  const hasChartData = dataKeys.length > 0;

  return (
    <Card className="h-md-100" style={{ position: 'relative' }}>
      {!hasChartData && (
        <Background
          image={corner6}
          className="bg-card"
          style={{
            borderTopRightRadius: '0.375rem',
            borderBottomRightRadius: '0.375rem',
            pointerEvents: 'none'
          }}
        />
      )}
      <Card.Header className="pb-0">
        <h6 className="mb-0 mt-2">{title}</h6>
      </Card.Header>
      <Card.Body
        as={Flex}
        alignItems="end"
        justifyContent="between"
        className="pt-0"
      >
        <div>
          {loading ? (
            <Spinner animation="border" size="sm" className="mb-2" />
          ) : error ? (
            <p className="text-danger fs--1 mb-1">Unable to load metric</p>
          ) : (
            <>
              <h2 className="fw-normal text-700 mb-1 lh-1">
                {totalCount ?? 0}
              </h2>
              {hasChartData ? (
                progressPercentStatus(dataValues)
              ) : (
                <span className="text-secondary fs--1">&nbsp;</span>
              )}
            </>
          )}
        </div>
        <div className="ps-0">
          {!loading && !error && hasChartData && (
            <BasicECharts
              echarts={echarts}
              options={getOptions(dataValues, dataKeys)}
              style={{ width: '8.5rem', height: 90 }}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

AdminDashboardMetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  totalCount: PropTypes.number,
  chartData: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object
};

export default AdminDashboardMetricCard;
